import { DataLayer } from '@piwikpro/react-piwik-pro';

import { graphql, Link } from 'gatsby';

import { Button, ButtonProps, Grid2 as Grid, styled } from '@mui/material';

import { ResponsiveGrid } from '@mewa/ui';

import { getMarketDomain } from '../../../../libs/config/markets-utils';
import { ComponentProps, PossibleContentSections } from '../fragment-and-types';
import { ContentLivePreviewConfig } from '../live-preview/types';

export const ButtonFragment = graphql`
  fragment ButtonFragment on STRAPI__COMPONENT_SHARED_BUTTON {
    title: Title
    linkUrl: LinkUrl
    piwikEventName: PiwikEventName
    variant: Variant
    position: Position
    strapi_component
  }
`;

export const componentName = 'shared.button';

export type ButtonData = {
  title: string;
  linkUrl: string;
  piwikEventName: string;
  variant: 'primary' | 'secondary';
  position: 'left' | 'center' | 'right';
  strapi_component: typeof componentName;
};

export type ButtonPreview = {
  strapi_component: typeof componentName;
  Title: string;
  LinkUrl: string;
  PiwikEventName: string;
  Variant: ButtonData['variant'];
  Position: ButtonData['position'];
};

export const isButton = (
  component: PossibleContentSections,
): component is ButtonData => {
  return component.strapi_component === componentName;
};

export const StrapiButton = ({
  linkUrl,
  variant,
  piwikEventName,
  position,
  title,
  conversion,
  sapForm,
}: ComponentProps<ButtonData>) => {
  const linkIsExternal = linkUrl?.startsWith('http');

  const sharedProps: Partial<ButtonProps> = {
    color: 'primary',
    variant: variant === 'primary' ? 'contained' : 'outlined',
    onClick: () => {
      if (piwikEventName) {
        DataLayer.push({ event: piwikEventName });
      }
    },
  };

  return (
    <div style={{ textAlign: position ?? 'left' }}>
      {linkUrl ? (
        <Button
          component={'a'}
          href={linkUrl}
          target={linkIsExternal ? '_blank' : undefined}
          rel={linkIsExternal ? 'noreferrer' : undefined}
          {...sharedProps}
        >
          {title}
        </Button>
      ) : sapForm ? (
        <Button
          onClick={() => {
            document.getElementById('sapForm')?.scrollIntoView({
              behavior: 'smooth',
            });
          }}
          variant="outlined"
        >
          {sapForm.scrollToFormButtonLabel}
        </Button>
      ) : (
        <Button component={Link} to={conversion.link} {...sharedProps}>
          {title}
        </Button>
      )}
    </div>
  );
};

export const blogConfig = {
  isComponent: isButton,
  Component: (props: ComponentProps<ButtonData>) => (
    <Container>
      <StrapiButton {...props}></StrapiButton>
    </Container>
  ),
  componentName,
};

export const campaignConfig = {
  isComponent: isButton,
  Component: (props: ComponentProps<ButtonData>) => (
    <ResponsiveGrid>
      <Grid size={12}>
        <StrapiButton {...props} />
      </Grid>
    </ResponsiveGrid>
  ),
  componentName,
};

export const previewConfig: ContentLivePreviewConfig = {
  componentName,
  populationParams: {},
  mapProperties: (button, market): ButtonData | null =>
    button.strapi_component === componentName
      ? {
          strapi_component: button.strapi_component,
          title: button.Title,
          linkUrl:
            button.LinkUrl && button.LinkUrl?.startsWith('http')
              ? button.LinkUrl
              : `${getMarketDomain(market)}${button.LinkUrl}`,
          piwikEventName: button.PiwikEventName,
          variant: button.Variant,
          position: button.Position,
        }
      : null,
};

const Container = styled('div')(({ theme }) => ({
  width: '80%',

  [theme.breakpoints.up('sm')]: {
    width: '50%',
  },

  [theme.breakpoints.up('md')]: {
    width: '60%',
  },

  [theme.breakpoints.up('lg')]: {
    width: '50%',
  },
}));
